import type { EnergyTariff, EnergyTariffBonus } from 'src/types/EnergyTariff';
import type { VerivoxOffer } from '../api/verivoxApi';

export interface TransformOptions {
	isSponsored?: boolean;
	preferResponsiveMsp?: boolean;
}

/**
 * Extracts bonuses from a Verivox offer response
 * @param offer - Verivox offer object
 * @returns Array of extracted bonuses
 */
export function extractBonuses(offer: VerivoxOffer | any): EnergyTariffBonus[] {
	const bonuses: EnergyTariffBonus[] = [];
	const anyOffer = offer as any;

	if (anyOffer.benefits && Array.isArray(anyOffer.benefits)) {
		for (const benefit of anyOffer.benefits) {
			const text = benefit.content?.text || benefit.text || '';
			if (
				text.toLowerCase().includes('neukunden') ||
				text.toLowerCase().includes('new customer')
			) {
				bonuses.push({
					type: 'newCustomer',
					description: text,
					percentage: benefit.percentage,
					amount: benefit.amount ? parseFloat(String(benefit.amount)) : undefined,
				});
			} else if (text.toLowerCase().includes('sofort') || text.toLowerCase().includes('instant')) {
				bonuses.push({
					type: 'instant',
					description: text,
					amount: benefit.amount ? parseFloat(String(benefit.amount)) : undefined,
				});
			}
		}
	}

	return bonuses;
}

/**
 * Transforms a Verivox offer to EnergyTariff format
 * @param offer - Verivox offer object
 * @param options - Optional transformation options
 * @returns Transformed EnergyTariff object
 */
export function transformOfferToTariff(
	offer: VerivoxOffer | any,
	options: TransformOptions = {},
): EnergyTariff {
	const anyOffer = offer as any;
	const { isSponsored = false, preferResponsiveMsp = false } = options;

	const totalCosts = parseFloat(String(offer.cost?.totalCost?.amount || 0)) || 0;
	const bonuses = extractBonuses(offer);

	// Extract remarks with proper transformation
	let remarks: any[] | undefined;
	if (anyOffer.remarks && Array.isArray(anyOffer.remarks)) {
		remarks = anyOffer.remarks.map((remark: any) => ({
			content: {
				text: remark.content?.text || remark.text || '',
				tooltip: remark.content?.tooltip || remark.tooltip,
			},
		}));
	} else if (anyOffer.remarks && Array.isArray(Object.values(anyOffer.remarks))) {
		remarks = anyOffer.remarks.map((remark: any) => ({
			content: {
				text: remark.content?.text || remark.text || '',
				tooltip: remark.content?.tooltip || remark.tooltip,
			},
		}));
	} else {
		remarks = undefined;
	}

	// Determine signup URL with preference support
	let signupUrl: string | undefined;
	if (preferResponsiveMsp) {
		signupUrl = anyOffer.signup?.responsiveMsp?.url || anyOffer.signup?.desktop?.url;
	} else {
		signupUrl = anyOffer.signup?.responsive?.url || anyOffer.signup?.desktop?.url;
	}

	return {
		id: offer.tariff?.id || '',
		name:
			offer.tariff?.longName ||
			offer.tariff?.content?.longName ||
			offer.tariff?.content?.text ||
			'Unknown Tariff',
		provider:
			offer.provider?.longName ||
			offer.provider?.content?.longName ||
			offer.provider?.content?.text ||
			'Unknown Provider',
		logoUrl: offer.provider?.logoUrl || undefined,
		totalCosts,
		pricePerKwh: parseFloat(String(offer.cost?.unitPrice?.amount || 0)) / 100 || 0,
		basePrice: parseFloat(String(offer.cost?.fixedCosts?.amount || 0)) || 0,
		signupUrl,
		isSponsored,
		providerInfo: offer.providerInfo || undefined,
		bonuses: bonuses.length > 0 ? bonuses : undefined,
		priceGuaranteeMonths: anyOffer.priceGuarantee?.months || anyOffer.contract?.priceGuaranteeMonths,
		initialTermMonths: anyOffer.contract?.initialTermMonths || anyOffer.initialTermMonths,
		isVerivoxVerified: anyOffer.isVerivoxVerified || anyOffer.verified === true,
		monthlyCost: totalCosts > 0 ? totalCosts / 12 : undefined,
		savings: anyOffer.cost?.savings || anyOffer.savings || undefined,
		cost: anyOffer.cost || undefined,
		remarks: remarks && remarks.length > 0 ? remarks : undefined,
	};
}
